/**
 * PendingInvitesModal — view + manage open invitations.
 *
 * Phase G.7.11 (sacred/change-doctrine.md §VIII):
 *   The inviter's view of their open thresholds — those they've Acknowledged
 *   who haven't yet stepped through. Each row shows the invitee's email,
 *   when invited, whether anonymously, and a private nickname (if assigned).
 *   Two actions per row: Resend the magic link, or Cancel the invitation.
 *
 * Cancel is a release: the Contact + auth.user (if any) are deleted; the
 * email is free to be re-invited (or to be used by someone else entirely).
 * Doctrinally aligned with Free-Will: the inviter may withdraw a gesture
 * that hasn't yet become a binding.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  usePendingInvitees,
  type PendingInviteeRow,
} from '@/hooks/usePendingInvitees';
import { useNicknames } from '@/hooks/useNicknames';
import { resolveTimezone, utcToLocalDateString } from '@/lib/dates';
import {
  OrchistSearchInput,
} from '@/components/OrchistSearch';
import { evaluateQuery, parseQuery } from '@/lib/fuzzy-search';
import { AnonymityToggleButton } from '@/components/AnonymityToggleButton';

export interface PendingInvitesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PendingInvitesModal({ isOpen, onClose }: PendingInvitesModalProps) {
  const { contact } = useAuth();
  const tz = resolveTimezone(contact?.timezone);
  const { rows, loading, error, refresh, cancelInvitation, resendInvitation } = usePendingInvitees();
  const { byTargetId: nicknamesByTargetId } = useNicknames();

  const [searchQuery, setSearchQuery] = useState('');
  const [actionState, setActionState] = useState<'idle' | 'busy'>('idle');
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (confirmCancelId) setConfirmCancelId(null);
        else onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, confirmCancelId]);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setActionState('idle');
      setFeedbackMessage(null);
      setErrorMessage(null);
      setConfirmCancelId(null);
      setResendingId(null);
      setCancellingId(null);
    }
  }, [isOpen]);

  // Apply FAB filter (fuzzy + boolean) over a per-row haystack of email,
  // displayName, nickname (if any), formatted sent date.
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const q = parseQuery(searchQuery);
    return rows.filter((r) => {
      const nick = nicknamesByTargetId.get(r.id);
      const haystack = [
        r.email,
        r.displayName,
        nick?.nickname ?? '',
        nick?.notes ?? '',
        utcToLocalDateString(r.invitationSentAt, tz) ?? '',
        r.invitationAnonymous ? 'anonymous' : '',
      ].join(' \n ');
      return evaluateQuery(haystack.toLowerCase(), q);
    });
  }, [rows, searchQuery, nicknamesByTargetId, tz]);

  if (!isOpen) return null;

  async function handleResend(row: PendingInviteeRow) {
    if (actionState === 'busy') return;
    setActionState('busy');
    setResendingId(row.id);
    setFeedbackMessage(null);
    setErrorMessage(null);
    try {
      await resendInvitation(row.email);
      setFeedbackMessage(`Magic link re-sent to ${row.email}.`);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Could not resend.');
    } finally {
      setActionState('idle');
      setResendingId(null);
    }
  }

  async function handleCancel(row: PendingInviteeRow) {
    if (actionState === 'busy') return;
    setActionState('busy');
    setCancellingId(row.id);
    setFeedbackMessage(null);
    setErrorMessage(null);
    try {
      await cancelInvitation(row.id);
      setFeedbackMessage(`Invitation to ${row.email} cancelled. The email is free.`);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Could not cancel.');
    } finally {
      setActionState('idle');
      setCancellingId(null);
      setConfirmCancelId(null);
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center px-6 py-12">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute inset-0 bg-paper/80 backdrop-blur-sm cursor-default"
      />

      <div className="relative w-full max-w-xl bg-paper border border-ink/20 shadow-xl p-8 space-y-6 max-h-[90vh] overflow-y-auto">
        <div className="text-center space-y-2">
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
            Open thresholds
          </p>
          <h2 className="display text-3xl font-medium leading-tight">
            <span className="italic text-accent">Pending invitations.</span>
          </h2>
          <p className="text-sm text-ink/55 italic font-display max-w-md mx-auto leading-relaxed">
            Those you have Acknowledged who have not yet stepped through.
            You may re-send the magic link, or cancel and free the email.
          </p>
        </div>

        {/* FAB search */}
        {rows.length > 5 && (
          <OrchistSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by email, nickname, date..."
            summary={
              searchQuery
                ? `${filteredRows.length} of ${rows.length} match`
                : undefined
            }
          />
        )}

        {/* Results */}
        {loading ? (
          <p className="text-sm italic text-ink/40 font-display">Reading...</p>
        ) : error ? (
          <p className="text-sm text-destructive italic font-display">
            Could not read pending invitations: {error.message}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm italic text-ink/40 font-display text-center py-6">
            No open thresholds. The Line waits for your next gesture.
          </p>
        ) : filteredRows.length === 0 ? (
          <p className="text-sm italic text-ink/40 font-display text-center py-4">
            No matches.
          </p>
        ) : (
          <ul className="space-y-3 list-none pl-0">
            {filteredRows.map((row) => {
              const nick = nicknamesByTargetId.get(row.id);
              const sentDate = utcToLocalDateString(row.invitationSentAt, tz);
              const isConfirmingCancel = confirmCancelId === row.id;
              return (
                <li key={row.id} className="border border-ink/15 px-4 py-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-sm truncate">{row.email}</p>
                      {nick && (
                        <p className="text-xs text-accent italic font-display">
                          you call them: <span className="text-ink not-italic">{nick.nickname}</span>
                        </p>
                      )}
                      <p className="text-[0.65rem] text-ink/45 italic mt-0.5">
                        {sentDate ? `Invited ${sentDate}` : 'Recently invited'}
                        {row.invitationAnonymous && (
                          <span className="ml-2 text-accent">· sent anonymously</span>
                        )}
                        {row.hasAuthUser && (
                          <span className="ml-2 text-ink/40">· clicked the link</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {!isConfirmingCancel ? (
                    <div className="flex flex-wrap gap-3 text-xs pt-1 items-baseline">
                      <button
                        type="button"
                        onClick={() => handleResend(row)}
                        disabled={actionState === 'busy'}
                        className="italic underline underline-offset-4 decoration-accent/40 hover:decoration-accent text-ink/75 hover:text-ink transition-colors disabled:opacity-50"
                      >
                        {resendingId === row.id ? 'Sending...' : 'Re-send magic link'}
                      </button>
                      <span className="text-ink/25">·</span>
                      <AnonymityToggleButton
                        inviteeId={row.id}
                        isAnonymous={row.invitationAnonymous}
                        onSuccess={refresh}
                      />
                      <span className="text-ink/25">·</span>
                      <button
                        type="button"
                        onClick={() => setConfirmCancelId(row.id)}
                        disabled={actionState === 'busy'}
                        className="italic underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 text-ink/55 hover:text-ink transition-colors disabled:opacity-50"
                      >
                        Cancel invitation
                      </button>
                    </div>
                  ) : (
                    <div className="border-t border-ink/10 pt-2 space-y-2">
                      <p className="text-xs italic text-ink/65 font-display leading-relaxed">
                        Cancel the invitation? The Contact + their auth identity
                        will be deleted. The email goes back to availability.
                        This cannot be undone &mdash; you'd need to invite again
                        from scratch.
                      </p>
                      <div className="flex gap-2 text-xs">
                        <button
                          type="button"
                          onClick={() => setConfirmCancelId(null)}
                          disabled={actionState === 'busy'}
                          className="px-3 py-1.5 border border-ink/20 text-ink/70 italic font-display hover:border-ink/50 hover:text-ink transition-colors disabled:opacity-50"
                        >
                          Keep invitation
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCancel(row)}
                          disabled={actionState === 'busy'}
                          className="px-3 py-1.5 bg-ink text-paper font-display tracking-wide hover:bg-ink/85 transition-colors disabled:opacity-50"
                        >
                          {cancellingId === row.id ? 'Cancelling...' : 'Yes, cancel'}
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {feedbackMessage && (
          <p className="text-sm text-accent italic font-display text-center" role="status">
            {feedbackMessage}
          </p>
        )}
        {errorMessage && (
          <p className="text-sm text-destructive italic font-display text-center" role="alert">
            {errorMessage}
          </p>
        )}

        <button
          type="button"
          onClick={onClose}
          className="w-full px-5 py-3 border border-ink/30 text-ink/70 font-display italic tracking-wide hover:border-ink/60 hover:text-ink transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  );
}
